import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useCategoryIndex } from '@/hooks/useCategoryIndex';
import type { useSpaceExpensesPage } from '@/hooks/queries/useSpaceExpensesPage';
import type { SpaceExpenseRow } from '@/lib/spaces/expenses';
import { dayHeading, isOverview, readFailed } from '@/lib/spaces/home';
import { spaceHrefForPeriod } from '@/lib/spaces/navigation';
import { groupItemsByDay, type DayGroup } from '@/lib/transactions/utils';
import type { SpaceDetail } from '@/types/spaces';
import { SettlementCard, SettlementFailure } from './SettlementParts';
import { SpaceExpenseDrawer } from './SpaceExpenseDrawer';
import { SpaceExpenseItem } from './SpaceExpenseList';

const TITLE = 'Últimas despesas';
// Espaços render `occurredAt` in UTC (`expenseDate`), so the day sections key on the UTC day too.
const utcDay = (expense: SpaceExpenseRow) => expense.occurredAt.slice(0, 10);

export function SpaceHomeRecent({ space, userId, period, recent }: Readonly<{
  space: SpaceDetail; userId: string; period: string; recent: ReturnType<typeof useSpaceExpensesPage>;
}>) {
  const categoryIndex = useCategoryIndex();
  const [selected, setSelected] = useState<SpaceExpenseRow | null>(null);
  const close = () => setSelected(null);
  const action = { label: 'Ver todas', href: spaceHrefForPeriod(space.id, 'expenses', period) };
  if (readFailed(recent, isOverview)) return <SettlementCard title={TITLE} action={action}><SettlementFailure message="Não foi possível carregar as últimas despesas." retry={recent.mutate} /></SettlementCard>;
  if (!recent.data) return <SettlementCard title={TITLE} action={action}><div aria-busy="true" aria-label="Carregando as últimas despesas" className="space-y-2"><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-12 rounded-xl" /></div></SettlementCard>;
  const { expenses: { items: expenses }, model } = recent.data;
  if (!expenses.length) return <SettlementCard title={TITLE} action={action}><p className="text-sm text-text-secondary">{space.kind === 'trip' ? 'Nada entrou na viagem ainda.' : 'Nada entrou no espaço neste mês ainda.'}</p></SettlementCard>;
  const renderExpense = (expense: SpaceExpenseRow) => <SpaceExpenseItem key={expense.id} space={space} userId={userId} expense={expense} model={model} categoryIndex={categoryIndex} onOpen={setSelected} density="compact" />;
  const renderDay = (day: DayGroup<SpaceExpenseRow>) => <div key={day.key}>
    <h3 className="font-mono text-2xs font-medium uppercase tracking-wider text-text-secondary">{dayHeading(day.key)}</h3>
    <ul className="divide-y divide-border-subtle">{day.items.map(renderExpense)}</ul>
  </div>;
  return <SettlementCard title={TITLE} action={action}>
    <div className="space-y-3">{groupItemsByDay(expenses, utcDay).map(renderDay)}</div>
    {selected ? <SpaceExpenseDrawer key={selected.id} space={space} userId={userId} expense={selected} model={space.model} close={close} /> : null}
  </SettlementCard>;
}
